import type { Profile as ProfileData, ProfileCustomization } from '../../lib/types';
import { TrainerAvatar } from '../avatar/TrainerAvatar';

/**
 * The trainer card.
 *
 * One component, used by the profile page *and* by the customization editor's
 * preview. That is the entire reason it exists as its own file: the first cut
 * of customization had the editor draw little swatches of what each option
 * looked like, which is a second implementation of the same appearance and
 * therefore a second thing to keep in step. A preview that renders the real
 * card cannot lie about what other people will see.
 *
 * Everything cosmetic arrives as a key and is applied as a class. Nothing here
 * decides what a nameplate looks like — `phase3.css` does — and nothing here
 * decides what a player is *allowed* to wear, which the server settled before
 * the payload was built.
 */

export interface TrainerCardProps {
  /**
   * `avatar` is optional so that a caller which has not been updated yet still
   * compiles and still renders — it falls back to `avatarSprite`, which is what
   * this card drew before Trainer Avatars existed. The fallback lives here
   * rather than at each call site for the usual reason: one answer to "what is
   * this person's face" is the whole point of the shared component.
   */
  profile: Pick<ProfileData, 'trainerName' | 'avatarSprite'> & {
    avatar?: ProfileData['avatar'] | null;
  };
  customization: Pick<ProfileCustomization, 'nameplate' | 'cardBorder' | 'theme' | 'frame'>;
  /**
   * The Featured Companion, already resolved and scoped to its owner.
   *
   * Structurally typed rather than taking a whole `Pokemon`: the profile has a
   * full one to hand and the editor has a four-field summary, and widening the
   * editor's payload to satisfy a type would mean shipping stats and IVs to a
   * screen that draws a sprite and a name.
   */
  featured?: {
    sprite: string;
    displayName: string;
    bond?: { level: number; isSoulbound: boolean } | null;
  } | null;
  /**
   * Everything below the name.
   *
   * The card is a *shell* rather than a whole profile on purpose. A profile
   * body carries things a preview has no business knowing about — the NPC
   * quote, the guild chip, the last-seen line — and copying that into the
   * editor to make a preview would be a second implementation of the profile
   * that drifts from the first. What is genuinely shared is the chrome: the
   * frame, the nameplate, the border and the theme wash, which is exactly the
   * set of things customization changes.
   */
  children?: React.ReactNode;
}

export function TrainerCard({
  profile,
  customization,
  featured,
  children,
}: TrainerCardProps) {
  return (
    <div
      className={[
        'tcard',
        `tcard--border-${customization.cardBorder}`,
        `tcard--theme-${customization.theme}`,
      ].join(' ')}
    >
      {/* The theme wash sits behind everything and is inert — a decorative
          layer that never intercepts a pointer, so a themed card is exactly as
          clickable as a plain one. */}
      <div className="tcard__theme" aria-hidden />

      <div className="tcard__body">
        <div className="tcard__avatar">
          {/* The Trainer Avatar, inside the badge-granted ring.
              `TrainerAvatar` reads the frame key from a data attribute so an
              unknown one renders no frame rather than breaking — a client can
              be older than the badge that granted it. Role styling belongs on
              this ring and on the card border; nothing tints the sprite. */}
          <TrainerAvatar
            avatar={
              profile.avatar ??
              (profile.avatarSprite
                ? {
                    kind: 'LEGACY_SPECIES',
                    url: profile.avatarSprite,
                    label: 'Pokémon avatar',
                    slug: null,
                    generation: null,
                    trainerClass: null,
                    category: null,
                    artistCredit: null,
                  }
                : null)
            }
            frame={customization.frame ?? 'none'}
            // 80 is exactly 1x the sprite source, so the card never resamples.
            size={80}
            loading="eager"
          />
        </div>

        <div className="tcard__main">
          {/* The nameplate is a band *behind* the name rather than a colour
              applied to it. Colouring the text would fight the palette and
              would fail the contrast floor on half the options; a band stays
              legible whatever is on it. */}
          <div className={`nameplate nameplate--${customization.nameplate}`}>
            <span className="nameplate__name">{profile.trainerName}</span>
          </div>

          {children}
        </div>

        {/* The Featured Companion. Deliberately on the card rather than in a
            panel below it: "the one Pokémon I want you to notice" belongs
            beside the trainer's own name, not in a list of six. */}
        {featured && (
          <div className="tcard__featured">
            <div className="tiny mono muted">FEATURED</div>
            <div
              className={[
                'tcard__featured-art',
                (featured.bond?.level ?? 0) >= 3 ? 'tcard__featured-art--glow' : '',
                featured.bond?.isSoulbound ? 'tcard__featured-art--soulbound' : '',
              ].join(' ')}
            >
              <img src={featured.sprite} alt="" className="pixel" width={72} height={72} />
            </div>
            <div className="tiny center">{featured.displayName}</div>
          </div>
        )}
      </div>

    </div>
  );
}
